import { TopoMap } from "../Topology/TopoMap";
import { TopoNode } from "../Topology/TopoNode";
import { DeviceNode } from "../Topology/DeviceNode";
import { SpanningTree } from "../Topology/SpanningTree";
import { List } from "../Utils/List";
import { Device } from "../Core/Device";
import { DeviceGroup } from "../Core/DeviceGroup";
import { DeviceGroupMember } from "../Core/DeviceGroupMember";
import { LayoutSettings, LayoutType } from "../Topology/LayoutSettings";

export class TopoNodePath {
    private _nodeList: TopoNode[] = [];
    private _topoMap: TopoMap = null;
    private _pathList: TopoNodePath[] = [];

    public TopoRootNode: TopoNode = null;

    public get Nodes(): TopoNode[] {
        return this._nodeList;
    }

    public get TopoMap(): TopoMap {
        return this._topoMap;
    }

    public get Paths(): TopoNodePath[] {
        return this._pathList;
    }

    constructor(topoMap: TopoMap, topoPath?: TopoNode[]) {
        this._topoMap = topoMap;
        if (topoPath && topoPath.length > 0) {
            for (const node of topoPath) {
                this._nodeList.push(node);
            }
        }
    }

    public Clear(): void {
        this._nodeList = [];
        this._pathList = [];
        this.TopoRootNode = null;
    }

    public get Count(): number {
        return this._nodeList.length;
    }

    public get IsRing(): boolean {
        if (!this._nodeList || this._nodeList.length <= 1)
            return false;
        if (!this._topoMap)
            return false;

        const firstNode = this._nodeList[0];
        const lastNode = this._nodeList[this._nodeList.length - 1];

        if (firstNode && lastNode) {
            if (firstNode === lastNode)
                return true;

            const adjNodes = this._topoMap.AdjacentNodes(lastNode);
            return adjNodes.Values.some(n => n === firstNode);
        }

        return false;
    }

    public get FirstNode(): TopoNode | null {
        if (this._nodeList && this._nodeList.length > 1)
            return this._nodeList[1];
        return null;
    }

    public get LastNode(): TopoNode | null {
        if (this._nodeList && this._nodeList.length > 1)
            return this._nodeList[this._nodeList.length - 1];
        return null;
    }

    public get RootNode(): TopoNode | null {
        if (this._nodeList && this._nodeList.length >= 1)
            return this._nodeList[0];
        return null;
    }

    public get RootDevice(): Device | null {
        const rootNode = this.RootNode;
        if (rootNode) {
            const deviceNode = rootNode instanceof DeviceNode ? rootNode as DeviceNode : null;
            if (deviceNode)
                return deviceNode.Device;
        }
        return null;
    }

    public Append(nodeOrPath: TopoNode | TopoNodePath): void {
        if (nodeOrPath == null)
            return;

        if (nodeOrPath instanceof TopoNodePath) {
            const path = nodeOrPath as TopoNodePath;
            if (path.Count <= 0)
                return;
            for (const node of path._nodeList) {
                if (!this._nodeList.includes(node))
                    this._nodeList.push(node);
            }
        } else {
            const node = nodeOrPath as TopoNode;
            if (!this._nodeList.includes(node))
                this._nodeList.push(node);
        }
    }

    public Insert(nodeOrPath: TopoNode | TopoNodePath): void {
        if (nodeOrPath == null)
            return;

        if (nodeOrPath instanceof TopoNodePath) {
            const path = nodeOrPath as TopoNodePath;
            if (path.Count <= 0)
                return;
            for (const node of path._nodeList) {
                if (!this._nodeList.includes(node))
                    this._nodeList.unshift(node);
            }
        } else {
            const node = nodeOrPath as TopoNode;
            if (!this._nodeList.includes(node))
                this._nodeList.unshift(node);
        }
    }

    public Contains(topoNode: TopoNode): boolean {
        if (topoNode == null)
            return false;
        return this._nodeList.includes(topoNode);
    }

    public Copy(): TopoNodePath {
        const newPath = new TopoNodePath(this._topoMap);
        newPath.Append(this);
        return newPath;
    }

    public Combine(newPath: TopoNodePath): TopoNodePath {
        const combinedPath = new TopoNodePath(this._topoMap);
        if (newPath) {
            const reversePath1 = this.Reverse();
            combinedPath.Append(reversePath1);
            combinedPath.Append(newPath);
        }
        return combinedPath;
    }

    public Reverse(): TopoNodePath {
        const newPath = new TopoNodePath(this._topoMap);
        for (const node of this._nodeList)
            newPath.Insert(node);
        return newPath;
    }

    public FindConnectedNode(topoNode: TopoNode): TopoNode | null {
        if (topoNode == null)
            return null;

        const adjNodes = this._topoMap.AdjacentNodes(topoNode);
        let connectedNode: TopoNode = null;
        adjNodes.ForEach((node) => {
            if (connectedNode != null)
                return;
            if (this.Contains(node))
                connectedNode = node;
        });
        return connectedNode;
    }

    public FindByDevice(device: Device): TopoNode | null {
        if (device == null)
            return null;

        for (const node of this._nodeList) {
            const deviceNode = node instanceof DeviceNode ? node as DeviceNode : null;
            if (deviceNode == null)
                continue;
            if (deviceNode.Device == null)
                continue;
            if (deviceNode.Device === device)
                return node;
        }
        return null;
    }

    public Except(tree: SpanningTree): TopoNodePath {
        const nodePath = new TopoNodePath(this._topoMap);
        if (tree == null || tree.Nodes.Count <= 0)
            return nodePath;

        tree.Nodes.ForEach((node) => {
            if (!this.Contains(node))
                nodePath.Append(node);
        });
        return nodePath;
    }

    public RingEdgePaths(centralRing: TopoNodePath): TopoNodePath[] {
        const edgePathList: TopoNodePath[] = [];

        if (centralRing) {
            for (const path of this._pathList) {
                let newPath: TopoNodePath = null;
                let ringNode: TopoNode = null;

                for (const node of path._nodeList) {
                    if (centralRing.Contains(node)) {
                        ringNode = node;
                        continue;
                    }
                    if (newPath == null) {
                        newPath = new TopoNodePath(centralRing.TopoMap);
                        newPath.Append(ringNode);
                        newPath.Append(node);
                        edgePathList.push(newPath);
                    } else {
                        newPath.Append(node);
                    }
                }
            }
        }
        return edgePathList;
    }

    public GetPaths(rootNode: TopoNode): TopoNodePath[] {
        const pathList: TopoNodePath[] = [];

        if (rootNode != null && this._topoMap != null) {
            const adjNodes = this._topoMap.AdjacentNodes(rootNode);
            adjNodes.ForEach((nextNode) => {
                const newQueue: TopoNode[] = [rootNode, nextNode];
                this._addToQueue(newQueue, nextNode);
                const newPath = new TopoNodePath(this._topoMap, newQueue);
                if (newPath.Count > 0)
                    pathList.push(newPath);
            });
        }
        return pathList;
    }

    public GetRingPaths(rootNode: TopoNode): TopoNodePath[] {
        if (rootNode != null) {
            if (!this._pathList || this._pathList.length <= 0) {
                this.BuildPaths(rootNode);
            }
        }
        return this._pathList;
    }

    public GetEdgePaths(centralRingPath: TopoNodePath): TopoNodePath[] {
        const pathList: TopoNodePath[] = [];

        if (centralRingPath != null && this._topoMap != null) {
            for (const topoNode of centralRingPath._nodeList) {
                const adjNodes = this._topoMap.AdjacentNodes(topoNode);
                const newQueue: TopoNode[] = [topoNode];

                adjNodes.ForEach((nextNode) => {
                    if (centralRingPath.Contains(nextNode))
                        return;
                    newQueue.push(nextNode);
                });

                if (newQueue.length > 1) {
                    const newPath = new TopoNodePath(this._topoMap, newQueue);
                    if (newPath.Count > 0)
                        pathList.push(newPath);
                }
            }
        }
        return pathList;
    }

    public GetCentralRing(rootNode: TopoNode, rebuildPaths: boolean = true): TopoNodePath | null {
        if (rootNode == null)
            return null;

        if (rebuildPaths)
            this.BuildPaths(rootNode);

        const ringPath = this._getLargestRing();
        if (ringPath != null)
            return ringPath;

        // No central ring — combine the 2 longest paths
        if (this._pathList.length >= 2) {
            return this._pathList[0].Combine(this._pathList[1]);
        } else if (this._pathList.length === 1) {
            return this._pathList[0];
        }

        return null;
    }

    public BuildPaths(rootNode: TopoNode): number {
        this._pathList = [];

        if (rootNode != null && this._topoMap != null) {
            this.TopoRootNode = rootNode;

            const adjNodes = this._topoMap.AdjacentNodes(rootNode, true);
            adjNodes.ForEach((nextNode) => {
                if (!this._nodeInAnyPath(nextNode)) {
                    const newPath = new TopoNodePath(this._topoMap);
                    newPath.Append(rootNode);
                    newPath.Append(nextNode);
                    this._pathList.push(newPath);
                    this._buildPath(newPath, nextNode);
                }
            });
        }

        const pathCount = this._pathList.length;
        if (pathCount > 1) {
            this._pathList.sort((a, b) => b.Count - a.Count);
        }
        return pathCount;
    }

    public static CreateTopoMap(nodePath: TopoNodePath): TopoMap | null {
        if (nodePath == null)
            return null;

        const rootNode = nodePath.RootNode;
        const rootDeviceNode = rootNode instanceof DeviceNode ? rootNode as DeviceNode : null;

        const newGroup = new DeviceGroup();
        if (rootDeviceNode != null)
            newGroup.AddMember(new DeviceGroupMember(rootDeviceNode.Device));

        const topoMap = new TopoMap(newGroup);

        const deviceList = new List<Device>();
        for (const node of nodePath._nodeList) {
            if (node === rootNode)
                continue;
            const deviceNode = node instanceof DeviceNode ? node as DeviceNode : null;
            if (deviceNode == null)
                continue;
            deviceList.Add(deviceNode.Device);
        }

        const radialLayoutSettings = new LayoutSettings();
        radialLayoutSettings.LayoutType = LayoutType.Radial;

        topoMap.AddSelectedNodeMembers(rootNode, deviceList, radialLayoutSettings);

        return topoMap;
    }

    // ---- Private helpers ----

    private _getLargestRing(): TopoNodePath | null {
        if (!this._pathList || this._pathList.length === 0)
            return null;

        const sorted = [...this._pathList].sort((a, b) => b.Count - a.Count);
        for (const path of sorted) {
            if (path.IsRing)
                return path;
        }
        return null;
    }

    private _nodeInAnyPath(topoNode: TopoNode): boolean {
        if (topoNode == null)
            return false;
        return this._pathList.some(p => p.Contains(topoNode));
    }

    private _buildPath(currentPath: TopoNodePath, baseNode: TopoNode): void {
        if (this._topoMap == null || currentPath == null || baseNode == null)
            return;

        const adjNodes = this._topoMap.AdjacentNodes(baseNode, true);
        if (adjNodes == null || adjNodes.Count === 0)
            return;

        const basePathCopy = currentPath.Copy();
        let addedNode: TopoNode = null;
        const nodesToAdd: TopoNode[] = [];

        adjNodes.ForEach((nextNode) => {
            if (currentPath.Contains(nextNode))
                return;

            if (addedNode == null) {
                addedNode = nextNode;
                currentPath.Append(addedNode);
            } else {
                if (!nodesToAdd.includes(nextNode))
                    nodesToAdd.push(nextNode);
            }
        });

        if (addedNode != null) {
            this._buildPath(currentPath, addedNode);
        }

        for (const nextNode of nodesToAdd) {
            if (currentPath.Contains(nextNode))
                continue;
            if (!this._nodeInAnyPath(nextNode)) {
                const newPath = basePathCopy.Copy();
                this._pathList.push(newPath);
                newPath.Append(nextNode);
                this._buildPath(newPath, nextNode);
            }
        }
    }

    private _addToQueue(queue: TopoNode[], node: TopoNode): void {
        const adjNodes = this._topoMap.AdjacentNodes(node);
        adjNodes.ForEach((nextNode) => {
            if (!queue.includes(nextNode)) {
                queue.push(nextNode);
                this._addToQueue(queue, nextNode);
            }
        });
    }
}
